import type {
  TerraUIMessage} from '@terra/shared-dto-chat-types';
import {
  DataMessageTypeEnum,
  TerraUIMessageDataPartName,
} from '@terra/shared-dto-chat-types';
import type { CoPilotTraceExportPayload } from './co-pilot-trace-export';
import {
  sanitizeCoPilotExportCsvCell,
  sanitizeCoPilotExportMarkdownPlainText,
} from './co-pilot-export-sanitize';
import { formatCoPilotTranscriptHeader } from './co-pilot-transcript-export';
import { formatHttpExchangePayloads } from './http-exchange-output.utils';
import { isHttpExchangeTool } from './tool-config.helpers';

export type CoPilotSessionHttpExchangeRow = {
  toolCallId: string;
  toolName: string;
  displayName: string;
  method: string;
  url: string;
  statusCode: string;
  request: string;
  response: string;
  error: string;
  timestamp: string;
};

function readRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function stringifyToolOutput(output: unknown): string {
  if (typeof output === 'string') {
    return output;
  }
  if (output !== undefined && output !== null) {
    return JSON.stringify(output);
  }
  return '';
}

function parseMethodUrl(request?: string): { method: string; url: string } {
  if (!request?.trim()) {
    return { method: '', url: '' };
  }
  const match = /^(\w+)\s+(\S+)/.exec(request.trim());
  return { method: match?.[1] ?? '', url: match?.[2] ?? '' };
}

function parseStatusCode(response?: string): string {
  if (!response?.trim()) {
    return '';
  }
  const match = /^HTTP\/[\d.]+\s+(\d+)/.exec(response.trim());
  return match?.[1] ?? '';
}

function buildTimelineExchangeKey(row: CoPilotSessionHttpExchangeRow): string {
  const timestampPart = row.timestamp || 'no-timestamp';
  const requestPart = row.request.slice(0, 120);
  const responsePart = row.response.slice(0, 120);
  return `timeline:${row.method}:${row.url}:${row.statusCode}:${timestampPart}:${requestPart}:${responsePart}`;
}

function registerHttpExchangeKeys(
  seen: Set<string>,
  row: CoPilotSessionHttpExchangeRow,
): void {
  if (row.toolCallId) {
    seen.add(`tool:${row.toolCallId}`);
    return;
  }
  seen.add(buildTimelineExchangeKey(row));
}

function isHttpExchangeDuplicate(
  seen: Set<string>,
  row: CoPilotSessionHttpExchangeRow,
): boolean {
  if (row.toolCallId) {
    return seen.has(`tool:${row.toolCallId}`);
  }
  return seen.has(buildTimelineExchangeKey(row));
}

function upsertHttpExchangeRow({
  rows,
  seen,
  row,
}: {
  rows: CoPilotSessionHttpExchangeRow[];
  seen: Set<string>;
  row: CoPilotSessionHttpExchangeRow;
}): void {
  if (isHttpExchangeDuplicate(seen, row)) {
    return;
  }
  registerHttpExchangeKeys(seen, row);
  rows.push(row);
}

function buildHttpExchangeRow({
  toolCallId,
  toolName,
  displayName,
  request,
  response,
  error,
  timestamp,
}: {
  toolCallId: string;
  toolName: string;
  displayName: string;
  request?: string;
  response?: string;
  error?: string;
  timestamp?: string;
}): CoPilotSessionHttpExchangeRow {
  const { method, url } = parseMethodUrl(request);
  return {
    toolCallId,
    toolName,
    displayName,
    method,
    url,
    statusCode: parseStatusCode(response),
    request: request ?? '',
    response: response ?? '',
    error: error ?? '',
    timestamp: timestamp ?? '',
  };
}

function isTimelineMirrorOfToolRow({
  existingRows,
  row,
}: {
  existingRows: CoPilotSessionHttpExchangeRow[];
  row: CoPilotSessionHttpExchangeRow;
}): boolean {
  if (row.toolCallId) {
    return false;
  }

  return existingRows.some(
    (existing) =>
      Boolean(existing.toolCallId) &&
      existing.request === row.request &&
      existing.response === row.response,
  );
}

export function extractCoPilotSessionHttpExchanges({
  messages,
  exportPayload,
}: {
  messages: TerraUIMessage[];
  exportPayload: CoPilotTraceExportPayload;
}): CoPilotSessionHttpExchangeRow[] {
  const rows: CoPilotSessionHttpExchangeRow[] = [];
  const seen = new Set<string>();

  for (const message of messages) {
    for (const part of message.parts) {
      if (part.type !== TerraUIMessageDataPartName) {
        continue;
      }

      const data = part.data;
      if (
        data.type !== DataMessageTypeEnum.ToolCallComplete ||
        !data.toolName ||
        !isHttpExchangeTool(data.toolName)
      ) {
        continue;
      }

      const args = readRecord(data.input) ?? {};
      const outputText = stringifyToolOutput(data.output);
      const { requestPayload, responsePayload, error } =
        formatHttpExchangePayloads(args, outputText);

      upsertHttpExchangeRow({
        rows,
        seen,
        row: buildHttpExchangeRow({
          toolCallId: data.toolCallId ?? '',
          toolName: data.toolName,
          displayName: data.toolName,
          request: requestPayload,
          response: responsePayload,
          error,
          timestamp:
            typeof data.timestamp === 'number'
              ? new Date(data.timestamp).toISOString()
              : undefined,
        }),
      });
    }
  }

  for (const entry of exportPayload.timeline) {
    if (entry.kind !== 'http_request') {
      continue;
    }

    const row = buildHttpExchangeRow({
      toolCallId: '',
      toolName: entry.displayName,
      displayName: entry.displayName,
      request: entry.request,
      response: entry.response,
      error: entry.error,
      timestamp: entry.timestamp,
    });

    if (isTimelineMirrorOfToolRow({ existingRows: rows, row })) {
      continue;
    }

    upsertHttpExchangeRow({
      rows,
      seen,
      row,
    });
  }

  return rows;
}

export function buildCoPilotSessionHttpExchangesCsv({
  rows,
}: {
  rows: CoPilotSessionHttpExchangeRow[];
}): string {
  const headers = [
    'toolCallId',
    'toolName',
    'displayName',
    'method',
    'url',
    'statusCode',
    'request',
    'response',
    'error',
    'timestamp',
  ];

  const lines = [headers.join(',')];
  for (const row of rows) {
    lines.push(
      [
        row.toolCallId,
        row.toolName,
        row.displayName,
        row.method,
        row.url,
        row.statusCode,
        row.request,
        row.response,
        row.error,
        row.timestamp,
      ]
        .map((cell) => sanitizeCoPilotExportCsvCell(String(cell)))
        .join(','),
    );
  }

  return lines.join('\n');
}

export function countCoPilotSessionHttpExchangeCsvRows(csv: string): number {
  const lines = csv.trim().split('\n');
  return Math.max(0, lines.length - 1);
}

function escapeMarkdownTableCell(value: string): string {
  return sanitizeCoPilotExportMarkdownPlainText(value)
    .replace(/\|/g, '\\|')
    .replace(/\n/g, ' ');
}

export function buildCoPilotSessionHttpExchangesMarkdown({
  title,
  threadId,
  rows,
  exportedAt = new Date().toISOString(),
  timeZone,
}: {
  title: string | null | undefined;
  threadId: string;
  rows: CoPilotSessionHttpExchangeRow[];
  exportedAt?: string;
  timeZone?: string;
}): string {
  const header = formatCoPilotTranscriptHeader({
    title,
    threadId,
    exportedAt,
    timeZone,
  });
  const lines = [
    header,
    '',
    `Exported ${rows.length} HTTP exchange${rows.length === 1 ? '' : 's'}.`,
    '',
  ];

  if (rows.length === 0) {
    return `${lines.join('\n')}\n`;
  }

  lines.push(
    '| Tool | Method | URL | Status | Timestamp |',
    '| --- | --- | --- | --- | --- |',
  );

  for (const row of rows) {
    lines.push(
      `| ${escapeMarkdownTableCell(row.displayName || row.toolName)} | ${escapeMarkdownTableCell(row.method)} | ${escapeMarkdownTableCell(row.url)} | ${escapeMarkdownTableCell(row.statusCode)} | ${escapeMarkdownTableCell(row.timestamp)} |`,
    );
  }

  return `${lines.join('\n')}\n`;
}
